import { films, type Film, type FilmType } from "@/mocks/films";
import { actors, type Actor } from "@/mocks/actors";
import { news, type NewsItem } from "@/mocks/news";

export const typeLabels: Record<FilmType, string> = {
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
};

export const typeDescriptions: Record<FilmType, string> = {
  movie:
    "汇聚院线佳片与热门话题电影，涵盖悬疑、科幻、动作、剧情等多种题材，帮你快速找到值得一看的好片。",
  tv: "从都市情感、年代情怀到古装武侠与海外剧集，收录正在热播与口碑长红的电视剧作品。",
  variety:
    "综艺真人秀、竞技挑战与生活慢综一网打尽，掌握每档节目的最新动态与嘉宾阵容。",
};

export const allRegions: string[] = Array.from(
  new Set(films.map((f) => f.region)),
).sort();

export const allYears: number[] = Array.from(
  new Set(films.map((f) => f.year)),
).sort((a, b) => b - a);

export const actorRegions: string[] = Array.from(
  new Set(actors.map((a) => a.region)),
).sort();

export function getFilmById(id: string): Film | undefined {
  return films.find((f) => f.id === id);
}

export function getActorById(id: string): Actor | undefined {
  return actors.find((a) => a.id === id);
}

export function getFilmsByType(type: FilmType): Film[] {
  return films.filter((f) => f.type === type);
}

export function getActorsByIds(ids: string[]): Actor[] {
  return ids
    .map((id) => getActorById(id))
    .filter((a): a is Actor => Boolean(a));
}

export function getFilmsByIds(ids: string[]): Film[] {
  return ids
    .map((id) => getFilmById(id))
    .filter((f): f is Film => Boolean(f));
}

export function getWorksByActor(actorId: string): Film[] {
  const actor = getActorById(actorId);
  if (!actor) return [];
  return getFilmsByIds(actor.works);
}

export function getNewsForFilm(filmId: string): NewsItem[] {
  return news.filter((n) => n.relatedFilm === filmId);
}

export function getNewsForActor(actorId: string): NewsItem[] {
  return news.filter((n) => n.relatedActor === actorId);
}

export function getNewsForFilmCast(film: Film): NewsItem[] {
  const byFilm = getNewsForFilm(film.id);
  const byCast = news.filter(
    (n) => n.relatedActor && film.cast.includes(n.relatedActor),
  );
  const merged = [...byFilm, ...byCast];
  return Array.from(new Map(merged.map((n) => [n.id, n])).values());
}

export function getRelatedFilms(film: Film, limit = 4): Film[] {
  const scored = films
    .filter((f) => f.id !== film.id)
    .map((f) => {
      let score = 0;
      if (f.type === film.type) score += 2;
      const sharedCast = f.cast.filter((c) => film.cast.includes(c)).length;
      score += sharedCast * 3;
      if (f.director === film.director) score += 3;
      const sharedTags = f.tags.filter((t) => film.tags.includes(t)).length;
      score += sharedTags;
      return { film: f, score };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || b.film.popularity - a.film.popularity);
  return scored.slice(0, limit).map((s) => s.film);
}

export function getRelatedActors(actor: Actor, limit = 4): Actor[] {
  const scored = actors
    .filter((a) => a.id !== actor.id)
    .map((a) => {
      let score = 0;
      const sharedWorks = a.works.filter((w) => actor.works.includes(w)).length;
      score += sharedWorks * 3;
      if (a.region === actor.region) score += 1;
      if (a.gender === actor.gender) score += 0;
      return { actor: a, score };
    })
    .sort((a, b) => b.score - a.score || b.actor.popularity - a.actor.popularity);
  return scored.slice(0, limit).map((s) => s.actor);
}

export interface SearchResult {
  films: Film[];
  actors: Actor[];
}

export function searchAll(query: string): SearchResult {
  const q = query.trim().toLowerCase();
  if (!q) return { films: [], actors: [] };
  const matchedFilms = films.filter(
    (f) =>
      f.title.toLowerCase().includes(q) ||
      f.director.toLowerCase().includes(q) ||
      f.tags.some((t) => t.toLowerCase().includes(q)),
  );
  const matchedActors = actors.filter((a) => a.name.toLowerCase().includes(q));
  return { films: matchedFilms, actors: matchedActors };
}

export function trendingFilms(limit = 8): Film[] {
  return [...films].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function trendingActors(limit = 6): Actor[] {
  return [...actors].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function latestNews(limit = 6): NewsItem[] {
  return [...news]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, limit);
}